import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { CheckCircle, Wifi, Clock, Download } from "lucide-react";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiPaper-root": {
    border: `1px solid ${theme.palette.mode === "dark" ? "#444" : "#28a745"}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.mode === "dark"
        ? "#111827"
        : theme.palette.background.paper,
    overflow: "visible",
  },
}));

export default function SuccessModal({ open, onClose, amount, title, message, transactionId }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer;
    if (open) {
      setTimeout(() => setVisible(true), 100);
      timer = setTimeout(() => {
        handleClose();
      }, 15000);
    } else {
      setVisible(false);
    }
    return () => clearTimeout(timer);
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const downloadReceipt = () => {
    const receiptContent = `
      Bundle Activation Receipt
      Status: SUCCESSFUL
      Transaction ID: ${transactionId}
      Date: ${new Date().toLocaleDateString()}
      Time: ${new Date().toLocaleTimeString()}
      Amount: ${amount} AFN
      
      ${message}
      
      Thank you for using AFRH Merchant Portal
    `;
    
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Bundle_Receipt_${transactionId}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <div className="w-[400px] text-center relative overflow-visible">
        <div className="w-full px-6 pt-6 flex items-center justify-center relative z-10">
          <div className="relative">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            
            <div className="absolute inset-0">
              <div className="absolute inset-0 border-4 border-green-200 rounded-full animate-ping"></div>
              <div className="absolute inset-0 border-4 border-green-300 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center mt-6">
          <h1 className="text-[24px] mb-2 font-bold text-gray-800">
            {title || "Bundle Activation Initiated"}
          </h1>
        </div>

        <div className="px-4">
          <p className="text-gray-600 mb-6">
            {message || "Your bundle activation has been initiated successfully. Our team will process it shortly."}
          </p>
        </div>

        <div className="flex items-center justify-center flex-col gap-1 py-3 mt-2 w-full rounded-md bg-gradient-to-r from-[#f0f9ff] to-[#f0fdf4] border border-[#d1fae5]">
          <div className="flex items-center gap-2 mb-2">
            <Wifi size={20} className="text-green-600" />
            <h3 className="text-[16px] font-semibold text-gray-800">
              Bundle Amount
            </h3>
          </div>
          <p className="text-xl font-bold text-[#CD0C02]">
            {amount} AFN
          </p>
          
          {transactionId && (
            <div className="mt-2 text-sm">
              <p className="text-gray-600">Transaction ID:</p>
              <p className="font-mono text-gray-800">{transactionId}</p>
            </div>
          )}
        </div>

        <div className="mt-4 p-3 bg-primary-50 rounded-lg border border-primary-100">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Clock size={16} className="text-primary-600" />
            <span className="text-sm font-medium text-primary-800">Processing Information</span>
          </div>
          <ul className="text-sm text-primary-700 text-left space-y-1">
            <li>• Will be processed by our backoffice team</li>
            <li>• You'll be notified upon completion</li>
            <li>• Check status in your activation history</li>
          </ul>
        </div>

        <div className="w-full flex items-center gap-3 mt-6">
          <button
            onClick={downloadReceipt}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Download size={18} />
            Receipt
          </button>
          <button
            onClick={handleClose}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-md bg-[#CD0C02] text-white hover:bg-[#b30b02] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}